import { select } from '@panel/commons/core/themev2';
import { site } from '@panel/commons/helpers';
import styled, { css } from 'styled-components';

const siteId = site.id;
const siteWithBanner = siteId.includes('BR');

const Wrapper = styled.div`
  background-color: white;
  display: flex;
  flex-direction: column;
  white-space: normal;
  max-width: ${({ device }) => (device === 'desktop' ? '620px' : '546px')};
  width: 100%;
  margin: auto;
  padding: 16px;
  ${({ device }) => {
    return (
      device === 'mobile' &&
      css`
        min-height: calc(100vh - ${siteWithBanner ? '90' : '58'}px);
      `
    );
  }}
`;

const Container = styled.div`
  padding-top: ${({ device }) => (device === 'mobile' ? 0 : 24)}px;
  padding-bottom: ${({ device }) => (device === 'mobile' ? 0 : 70)}px;
  background-color: ${select('neutralGray')};
  width: 100%;
  height: 100%;
  min-height: calc(100vh - ${siteWithBanner ? '90' : '58'}px);
  margin: auto;
  @media (max-width: 1280px) {
    margin-top: ${siteWithBanner ? '90' : '58'}px;
  }
`;

const Header = styled.div`
  font-family: Hind;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  justify-content: space-between;
  > div {
    display: flex;
    gap: 10px;
  }
`;

const StatusPill = styled.div`
  font-family: Hind;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  display: flex;
  align-items: center;
  gap: 5px;
  color: ${({ status }) => select(stateFontColors[status || 'DEFAULT'])};
`;

const stateFontColors = {
  ONLINE: 'snackGreen',
  DRAFT: 'darkGray',
  NEAR_END: 'brownYellow',
  OFFLINE: 'negative',
  WITHOUT_CREDIT: 'negative',
  DEFAULT: 'black'
};

const Pill = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ status }) => select(stateFontColors[status || 'DEFAULT'])};
`;

const MapMiniContainer = styled.div``;

const ImgFrame = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  background-image: url(${({ url }) => url});
  background-size: contain;
  margin-right: 16px;
  ${({ isMobile }) => {
    return (
      isMobile &&
      css`
        width: 72px;
        height: 72px;
        margin-right: 8px;
      `
    );
  }}
`;

const CardFrame = styled.div`
  border: 1px solid ${select('neutralDarkGray')};
  border-radius: 8px;
  width: 100%;
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  ${({ device }) => {
    return device === 'mobile'
      ? css`
          padding: 8px;
          flex-direction: column;
        `
      : css`
          align-items: center;
        `;
  }};
`;

const EditButton = styled.div`
  text-align: center;
  font-family: Hind;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 130%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  > span {
    cursor: pointer;
    > img {
      margin-left: 5px;
    }
  }
  :hover {
    background-color: ${select('neutralDarkGray')};
  }
  ${({ device }) => {
    return (
      device === 'mobile' &&
      css`
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 8px;
        margin-top: 16px;
      `
    );
  }}
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  ${({ needChangeToRow, isMobile }) => {
    return needChangeToRow
      ? css`
          flex-direction: row;
          width: 100%;
          justify-content: center;
        `
      : isMobile
      ? css`
          flex-direction: column-reverse;
        `
      : '';
  }}
`;

const Divider = styled.div`
  height: 100%;
  width: 1px;
  background-color: ${select('neutralDarkGray')};
  margin: 0 8px;
  max-height: 22px;
  min-height: 20px;
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  ${({ orientation }) => {
    return css`
      flex-direction: ${orientation || 'row'};
      align-items: ${({ alignItems }) => alignItems || 'flex-start'};
    `;
  }}
  > strong {
    margin-right: 4px;
  }
`;

const CardSimpleItemContainer = styled.div`
  /* min-height: 24px; */
  padding-top: 4px;
  min-width: 0;
  padding-bottom: 4px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${({ withDivider }) =>
    withDivider &&
    css`
      border-right: 1px solid ${select('neutralDarkGray')};
      padding-right: 8px;
      margin-right: 8px;
    `};
  ${({ isMobile }) => {
    return (
      isMobile &&
      css`
        flex-direction: row-reverse;
        gap: 6px;
        max-width: 100%;
        padding-top: 0;
      `
    );
  }}
`;

const CardSimpleItemTitle = styled.div`
  font-family: Hind;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ isMobile }) => {
    return (
      isMobile &&
      css`
        flex: 1;
      `
    );
  }}
  ${({ isEmpty }) => {
    return (
      isEmpty &&
      css`
        color: ${select('midDarkGrayAux')};
      `
    );
  }}
`;

const CardSimpleItemLabel = styled.div`
  font-family: Hind;
  font-size: 12px;
  font-style: normal;
  font-weight: ${({ notDimmed }) => select(notDimmed ? '400' : '500')};
  line-height: 16px;
  color: ${({ notDimmed }) => select(notDimmed ? 'black' : 'midDarkGrayAux')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DescriptionCardContainer = styled.div``;

const DescriptionCardTitle = styled.div`
  font-family: Hind;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  margin-bottom: 10px;
  white-space: break-spaces;
  ${({ isEmpty }) => {
    return (
      isEmpty &&
      css`
        color: ${select('midDarkGrayAux')};
      `
    );
  }}
`;

const DescriptionCardText = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  text-overflow: ellipsis;
  font-family: Hind;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  white-space: pre-wrap;
  ${({ isEmpty }) => {
    return (
      isEmpty &&
      css`
        color: ${select('midDarkGrayAux')};
      `
    );
  }}
`;

const CardDividerContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  overflow: hidden;
`;

const CardEmptyContainer = styled.div``;

const CardWrapper = styled.div`
  ${({ isMobile }) => {
    return (
      !isMobile &&
      css`
        display: flex;
        height: 100%;
      `
    );
  }}
  ${({ needCenterMobile }) => {
    return (
      needCenterMobile &&
      css`
        display: flex;
        flex-direction: column;
        justify-content: center;
        height: 72px;
      `
    );
  }}
`;

const LoaderWrapper = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: rgb(256, 256, 256, 0.6);
  z-index: 200;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const ContainerOverFlow = styled.div`
  white-space: ${({ device }) => (device === 'mobile' ? 'normal' : 'nowrap')};
  overflow: hidden;
  text-overflow: ellipsis;
`;

export {
  Container,
  Header,
  Wrapper,
  StatusPill,
  MapMiniContainer,
  ImgFrame,
  CardFrame,
  EditButton,
  Footer,
  Pill,
  CardContent,
  Divider,
  CardSimpleItemContainer,
  CardSimpleItemTitle,
  CardSimpleItemLabel,
  DescriptionCardContainer,
  DescriptionCardTitle,
  DescriptionCardText,
  CardDividerContainer,
  CardWrapper,
  LoaderWrapper,
  CardEmptyContainer,
  ContainerOverFlow
};
